<script lang="ts" setup>
import type { TestMailOptions } from "~/stores/mail-store";
import {
    faEnvelope,
    faSpinnerThird,
    faTimes,
} from "@fortawesome/pro-light-svg-icons";
import { faPaperPlane } from "@fortawesome/pro-regular-svg-icons";
import type { Locale } from "~/models/Localization";

interface MailTestModalProps extends TestMailOptions {
    id: string;
    beforeSend?: Promise<() => void> | (() => Promise<void>);
}

const show = defineModel<boolean>("show", { default: false });
const props = defineProps<MailTestModalProps>();
const builder = useMailStore();
const { user } = useUser();

const INITIAL_STATE = {
    to_first_name: "",
    to_last_name: "",
    to_email: "",
    locale: useCloneDeep(builder.locale) as Locale,
};

const state =
    ref<Pick<TestMailOptions, "to_first_name" | "to_last_name" | "to_email"> & { locale: Locale }>(
        INITIAL_STATE,
    );

const sending = ref<boolean>(false);

async function send() {
    sending.value = true;

    if (props.beforeSend) {
        if (typeof props.beforeSend === "function") {
            await props.beforeSend();
        } else {
            await props.beforeSend;
        }
    }

    builder
        .sendTestEmail(props.id, {
            data: props.data,
            to_first_name: state.value.to_first_name,
            to_last_name: state.value.to_last_name,
            to_email: state.value.to_email,
            automation_id: props.automation_id,
            brand_id: props.brand_id,
            step_id: props.step_id,
            locale_id: state.value.locale.id,
        })
        .finally(() => {
            state.value = INITIAL_STATE;
            show.value = false;
            sending.value = false;
        });
}
</script>

<template>
    <UModal v-model="show">
        <UCard>
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="font-display text-lg flex gap-4 items-center">
                        <FontAwesomeIcon :icon="faEnvelope" />
                        <span>Test Email</span>
                    </h3>
                    <UButton color="gray" size="sm" square variant="ghost" @click="show = false">
                        <FontAwesomeIcon :icon="faTimes" size="lg" />
                    </UButton>
                </div>
                <p class="text-sm mt-2 opacity-50">
                    Send a test mail to yourself based on the selected mail
                    template. HireData will attempt to fill in all template
                    fields with realistic data.
                </p>
            </template>
            <UForm :state="state" class="flex flex-col gap-4">
                <div class="grid grid-cols-2 gap-4">
                    <UFormGroup hint="Optional" label="First name" name="first_name" size="md">
                        <UInput v-model="state.to_first_name" :placeholder="user.first_name" name="first_name"
                            size="lg" />
                    </UFormGroup>
                    <UFormGroup hint="Optional" label="Last name" name="last_name" size="md">
                        <UInput v-model="state.to_last_name" :placeholder="user.last_name" name="last_name" size="lg" />
                    </UFormGroup>
                </div>
                <UFormGroup hint="Optional" label="Email" name="email" size="md">
                    <UInput v-model="state.to_email" :placeholder="user.email" name="email" size="lg"
                        value-attribute="key" />
                </UFormGroup>
                <AppLanguageSelector size="lg" v-model="state.locale" v-if="builder.translations?.length"
                    :allowed-locales="builder.translations.map(t => t.locale?.id) ?? []" allows-empty none-label="Default" />
            </UForm>
            <template #footer>
                <UButton block size="lg" @click="send">
                    <template #leading>
                        <FontAwesomeIcon :icon="sending ? faSpinnerThird : faPaperPlane" :spin="sending" />
                    </template>
                    Send test email
                </UButton>
            </template>
        </UCard>
    </UModal>
</template>